import {useAuthBootstrap} from './useAuthBootstrap';
import {installedPlugins} from '../components/composer-model';
import type {ComposerSkill,ComposerPlugin} from '../components/composer-model';
import {useVisualizeSettings} from './useVisualizeSettings';
import {useCallback,useEffect,useRef,useState} from 'react';
import type {FileRecord,ModelInfo,ThreadRecord,User,WorkspaceStorageUsage,LibrarySnapshot,LibraryItemRef} from '@reados/contracts';
import {api,ApiError,errorMessage} from '../api';
import {uploadStorageIssue,validateStorageUsage} from '../storage';
import {allowedModelCatalog,MODEL_ACCESS_REFRESH_EVENT} from '../ai-usage';
import {BALANCE_UPDATED_EVENT} from '../top-up';
import {newerLibrarySnapshot} from '../library';
import {mergeThreadCatalog,mergeThreadMetadata} from '../thread-metadata';
import {chatStateKey,readDraftAttachments,saveDraftAttachments} from '../conversation-attachments';
import type {UploadProgressHandler} from '../upload-progress';

type WorkspaceSession={userId:string|undefined;generation:number};
export function useWorkspace(){
 const [user,setUserState]=useState<User|null>(null),[files,setFiles]=useState<FileRecord[]>([]),[threads,setThreads]=useState<ThreadRecord[]>([]),[models,setModels]=useState<ModelInfo[]>([]),[error,setErrorState]=useState(''),[health,setHealth]=useState<{ok:boolean;sandboxReady?:boolean;modelConfigured?:boolean}|null>(null);
 const [skills,setSkills]=useState<ComposerSkill[]>(),[plugins,setPlugins]=useState<ComposerPlugin[]>();
 const visualizeSettings=useVisualizeSettings(user?.id);
 const [storage,setStorage]=useState<WorkspaceStorageUsage|null>(null),[storageLoading,setStorageLoading]=useState(false),[storageError,setStorageError]=useState('');
 const [library,setLibrary]=useState<LibrarySnapshot|null>(null),[libraryLoading,setLibraryLoading]=useState(false),[libraryBusy,setLibraryBusy]=useState(false),[libraryError,setLibraryError]=useState('');
 const libraryRead=useRef<{owner:WorkspaceSession;controller:AbortController;promise:Promise<LibrarySnapshot>}|null>(null);
 const libraryRefreshTimer=useRef<ReturnType<typeof setTimeout>|null>(null);
 const libraryValue=useRef<LibrarySnapshot|null>(null),libraryRequest=useRef(0),catalogEpoch=useRef(0),libraryMutation=useRef<WorkspaceSession|null>(null);
 const storageRequest=useRef(0),session=useRef<WorkspaceSession>({userId:undefined,generation:0});
 const modelRequest=useRef(0),modelRead=useRef<{owner:WorkspaceSession;controller:AbortController}|null>(null);
 // Every render's callbacks belong to this exact login, including A -> B -> A.
 const owner=session.current;
 const isCurrent=useCallback(()=>session.current===owner&&owner.userId!==undefined,[owner]);
 const assertCurrent=useCallback(()=>{if(!isCurrent())throw new Error('登录状态已变化，请重新操作。');},[isCurrent]);
 const applyUser=useCallback((next:User|null)=>{
  session.current={userId:next?.id,generation:session.current.generation+1};
  modelRequest.current++;modelRead.current?.controller.abort();modelRead.current=null;
  libraryRead.current?.controller.abort();libraryRead.current=null;
  if(libraryRefreshTimer.current!==null){clearTimeout(libraryRefreshTimer.current);libraryRefreshTimer.current=null;}
  storageRequest.current++;libraryRequest.current++;catalogEpoch.current++;libraryValue.current=null;libraryMutation.current=null;
  setLibrary(null);setLibraryError('');setLibraryLoading(!!next);setLibraryBusy(false);
  setUserState(next);setFiles([]);setThreads([]);setModels([]);setSkills(undefined);setPlugins(undefined);setHealth(null);setErrorState('');setStorage(null);setStorageError('');setStorageLoading(!!next);
 },[]);
 const authBootstrap=useAuthBootstrap(applyUser),setUser=authBootstrap.setUser,initializing=authBootstrap.state.phase!=='ready';
 // An old Workspace catch handler must not publish its error in the next login.
 const setError=useCallback((message:string)=>{if(session.current===owner)setErrorState(message);},[owner]);
 const refreshStorage=useCallback(async()=>{
  assertCurrent();
  const requestId=++storageRequest.current;
  setStorageLoading(true);
  try{
   const usage=validateStorageUsage(await api.storage());
   assertCurrent();
   if(requestId===storageRequest.current){setStorage(usage);setStorageError('');}
   return usage;
  }catch(value){
   if(isCurrent()&&requestId===storageRequest.current)setStorageError(errorMessage(value));
   throw value;
  }finally{if(isCurrent()&&requestId===storageRequest.current)setStorageLoading(false);}
 },[assertCurrent,isCurrent]);
 const acceptLibrary=useCallback((next:LibrarySnapshot)=>{
  assertCurrent();
  const accepted=newerLibrarySnapshot(libraryValue.current,next);
  libraryValue.current=accepted;setLibrary(accepted);setFiles(accepted.files);
  setLibraryError(accepted.sync?.status==='error'?(accepted.sync.message||'资料库同步失败，请重新加载。'):'');
  return accepted;
 },[assertCurrent]);
 const refreshLibrary=useCallback(()=>{
  if(!isCurrent())return Promise.reject(new Error('登录状态已变化，请重新操作。'));
  if(libraryRead.current?.owner===owner)return libraryRead.current.promise;
  const requestId=++libraryRequest.current,controller=new AbortController();setLibraryLoading(true);
  const promise=(async()=>{
  try{
   // The library response owns layout, files and thread membership. Share one read
   // across every visible chat, and only retry if a local mutation crossed it.
   for(let attempt=0;attempt<3;attempt++){
    const epoch=catalogEpoch.current;
    const snapshot=await api.library(controller.signal);assertCurrent();
    if(epoch!==catalogEpoch.current)continue;
    if(libraryValue.current&&snapshot.revision<libraryValue.current.revision)return libraryValue.current;
    const accepted=acceptLibrary(snapshot);setThreads(current=>mergeThreadCatalog(current,snapshot.threads));return accepted;
   }
   // Frequent local acknowledgements can keep invalidating a read. Preserve
   // their accepted state; the normal background poll will reconcile again.
   if(libraryValue.current)return libraryValue.current;
   throw new Error('资料库正在其他位置更新，请稍后重新加载。');
  }catch(value){if(isCurrent()&&requestId===libraryRequest.current)setLibraryError(errorMessage(value));throw value;}
  finally{if(libraryRead.current?.controller===controller)libraryRead.current=null;if(isCurrent()&&requestId===libraryRequest.current)setLibraryLoading(false);}
  })();
  libraryRead.current={owner,controller,promise};return promise;
 },[assertCurrent,isCurrent,acceptLibrary,owner]);
 useEffect(()=>{
  if(!isCurrent())return;
  const poll=()=>{if(document.visibilityState!=='visible'||!isCurrent()||libraryMutation.current===owner||libraryRead.current?.owner===owner)return;void refreshLibrary().catch(()=>{});};
  const interval=window.setInterval(poll,10_000);document.addEventListener('visibilitychange',poll);
  return()=>{window.clearInterval(interval);document.removeEventListener('visibilitychange',poll);};
 },[isCurrent,refreshLibrary,owner]);
 async function mutateLibrary(operation:(revision:number)=>Promise<LibrarySnapshot>){
  assertCurrent();
  if(libraryMutation.current===owner)throw new Error('正在保存资料库，请稍候。');
  libraryMutation.current=owner;catalogEpoch.current++;setLibraryBusy(true);setLibraryError('');
  try{
   const current=libraryValue.current??await refreshLibrary();assertCurrent();
   const next=await operation(current.revision);assertCurrent();return acceptLibrary(next);
  }catch(value){
   assertCurrent();
   if(value instanceof ApiError&&value.status===409&&value.code==='LIBRARY_CONFLICT'){
    try{await refreshLibrary();}catch{assertCurrent();throw new Error('资料库已变化，但刷新失败；请刷新目录后重试。');}
    assertCurrent();throw new Error('资料库已在其他位置更新，已刷新目录，请重新操作。');
   }
   throw value;
  }finally{if(isCurrent()&&libraryMutation.current===owner){catalogEpoch.current++;libraryMutation.current=null;setLibraryBusy(false);}}
 }
 async function moveLibrary(entry:LibraryItemRef,parentId:string|null,before:LibraryItemRef|null=null){
  return mutateLibrary(revision=>api.moveLibrary(entry,parentId,before,revision));
 }
 async function createLibraryFolder(name:string,parentId:string|null){
  return mutateLibrary(revision=>api.createLibraryFolder(name,parentId,revision));
 }
 async function deleteLibraryFolder(folderId:string){
  return mutateLibrary(async revision=>{
   catalogEpoch.current++;
   const next=await api.deleteLibraryFolder(folderId,revision);assertCurrent();
   catalogEpoch.current++;return next;
  });
 }
 async function renameLibrary(entry:LibraryItemRef,name:string){
  if(entry.kind!=='thread')return mutateLibrary(revision=>api.renameLibrary(entry,name,revision));
  assertCurrent();
  if(libraryMutation.current===owner)throw new Error('正在保存资料库，请稍候。');
  libraryMutation.current=owner;setLibraryBusy(true);setLibraryError('');
  try{
   catalogEpoch.current++;
   const {thread}=await api.renameThread(entry.id,name);assertCurrent();
   if(thread.id!==entry.id)throw new Error('服务端返回的对话不匹配，请刷新后重试。');
   catalogEpoch.current++;
   // Keep computed list-only preview metadata; the PATCH response is lightweight.
   setThreads(current=>current.some(item=>item.id===thread.id)?current.map(item=>item.id===thread.id?mergeThreadMetadata(item,thread):item):[thread,...current]);
   return thread;
  }finally{if(isCurrent()&&libraryMutation.current===owner){libraryMutation.current=null;setLibraryBusy(false);}}
 }
 const refreshThreads=useCallback((thread?:ThreadRecord)=>{
  if(!isCurrent())return;
  if(thread){
   // Exact metadata from the conversation channel needs no second network read.
   // Invalidate an older catalog read and retain its existing row/preview identity.
   catalogEpoch.current++;setThreads(current=>current.map(item=>item.id===thread.id?mergeThreadMetadata(item,thread):item));
  }else if(libraryRefreshTimer.current===null){
   // One filesystem change is broadcast on several conversation streams.
   // Coalesce that burst before fetching the shared current catalog/status.
   libraryRefreshTimer.current=setTimeout(()=>{libraryRefreshTimer.current=null;if(isCurrent()){catalogEpoch.current++;void refreshLibrary().catch(()=>{});}},100);
  }
 },[isCurrent,refreshLibrary]);
 const refreshModels=useCallback(async()=>{
  assertCurrent();const requestId=++modelRequest.current,controller=new AbortController();
  modelRead.current?.controller.abort();modelRead.current={owner,controller};
  try{
   const value=await api.models(controller.signal);
   if(!isCurrent()||requestId!==modelRequest.current)return null;
   setModels(allowedModelCatalog(value.models,value.defaultModelId));return value;
  }catch(value){if(!isCurrent()||requestId!==modelRequest.current)return null;throw value;}
  finally{if(modelRead.current?.controller===controller)modelRead.current=null;}
 },[assertCurrent,isCurrent,owner]);
 useEffect(()=>{
  if(!isCurrent())return;
  const poll=()=>{if(document.visibilityState==='visible'&&isCurrent())void refreshModels().catch(()=>{});};
  const redeemed=(event:Event)=>{if((event as CustomEvent<{userId?:string}>).detail?.userId===owner.userId&&isCurrent())void refreshModels().catch(()=>{});};
  const interval=window.setInterval(poll,30_000);window.addEventListener('focus',poll);document.addEventListener('visibilitychange',poll);
  window.addEventListener(BALANCE_UPDATED_EVENT,redeemed);window.addEventListener(MODEL_ACCESS_REFRESH_EVENT,poll);
  return()=>{window.clearInterval(interval);window.removeEventListener('focus',poll);document.removeEventListener('visibilitychange',poll);window.removeEventListener(BALANCE_UPDATED_EVENT,redeemed);window.removeEventListener(MODEL_ACCESS_REFRESH_EVENT,poll);if(modelRead.current?.owner===owner){modelRequest.current++;modelRead.current.controller.abort();modelRead.current=null;}};
 },[isCurrent,refreshModels,owner]);
 const refresh=useCallback(async()=>{
  assertCurrent();
  const results=await Promise.allSettled([refreshModels(),api.health(),api.skills(),api.plugins(),refreshLibrary()]);
  assertCurrent();
  if(results[0].status==='rejected')setErrorState(errorMessage(results[0].reason));
  if(results[1].status==='fulfilled')setHealth(results[1].value);
  if(results[2].status==='fulfilled')setSkills(results[2].value.skills.map(item=>({id:item.path,name:item.name,description:item.description,sandboxPath:item.path,enabled:item.enabled})));
  if(results[3].status==='fulfilled')setPlugins(installedPlugins(results[3].value));
 },[assertCurrent,refreshLibrary,refreshModels]);
 useEffect(()=>()=>{session.current={userId:undefined,generation:session.current.generation+1};libraryRead.current?.controller.abort();if(libraryRefreshTimer.current!==null){clearTimeout(libraryRefreshTimer.current);libraryRefreshTimer.current=null;}},[]);
 useEffect(()=>{if(isCurrent())void refresh().catch(()=>{});},[isCurrent,refresh]);
 useEffect(()=>{
  if(!isCurrent())return;
  const update=()=>{if(document.visibilityState==='visible'&&isCurrent())void refreshStorage().catch(()=>{});};
  update();
  const interval=window.setInterval(update,30_000);
  document.addEventListener('visibilitychange',update);
  return()=>{storageRequest.current++;window.clearInterval(interval);document.removeEventListener('visibilitychange',update);};
 },[isCurrent,refreshStorage]);
 async function createThread(title?:string){
  assertCurrent();catalogEpoch.current++;const value=await api.createThread(title);assertCurrent();catalogEpoch.current++;setThreads(current=>[value.thread,...current]);void refreshLibrary().catch(()=>{});return value.thread;
 }
 async function logout(){
  if(!isCurrent())return;
  // Cancel old work before the logout request can change the cookie.
  const loggingOut:WorkspaceSession={userId:undefined,generation:owner.generation+1};session.current=loggingOut;
  modelRequest.current++;modelRead.current?.controller.abort();modelRead.current=null;
  libraryRead.current?.controller.abort();libraryRead.current=null;
  if(libraryRefreshTimer.current!==null){clearTimeout(libraryRefreshTimer.current);libraryRefreshTimer.current=null;}
  storageRequest.current++;libraryRequest.current++;libraryMutation.current=null;setLibraryBusy(false);setLibraryLoading(false);setStorageLoading(false);
  try{await api.logout();if(session.current===loggingOut)setUser(null);}
  catch(value){if(session.current===loggingOut){
   session.current={userId:owner.userId,generation:loggingOut.generation+1};
   // Rebind callbacks even when a repeated logout returns the same error and
   // loading was already false, so React would otherwise bail out.
   setUserState(current=>current?{...current}:current);
   setErrorState(errorMessage(value));
  }}
 }
 async function upload(list:FileList|File[],parentId:string|null=null,onProgress?:UploadProgressHandler,destination?:'chat'){
  assertCurrent();
  const selected=Array.from(list),results:FileRecord[]=[],failures:string[]=[];
  if(!selected.length)return results;
  const totalBytes=selected.reduce((total,file)=>total+file.size,0);
  let uploadedBytes=0;
  onProgress?.({phase:'checking',uploadedBytes:0,totalBytes,bytesPerSecond:null,determinate:true,fileName:selected[0]!.name,fileIndex:1,fileCount:selected.length});
  let usage:WorkspaceStorageUsage;
  try{usage=await refreshStorage();}catch{assertCurrent();throw new Error('暂时无法确认工作区剩余空间，请稍后重试上传。');}
  assertCurrent();
  const issue=uploadStorageIssue(usage,selected);
  if(issue)throw new Error(issue);
  try{
   for(const [index,file] of selected.entries()){
    assertCurrent();
    let currentBytes=0;
    try{
     catalogEpoch.current++;const value=await api.upload(file,onProgress?progress=>{
      if(!isCurrent())return;
      currentBytes=progress.uploadedBytes;
      onProgress({...progress,uploadedBytes:uploadedBytes+currentBytes,totalBytes,fileName:file.name,fileIndex:index+1,fileCount:selected.length});
     }:undefined,parentId,destination);assertCurrent();catalogEpoch.current++;if(!destination)setFiles(current=>[value.file,...current]);results.push(value.file);

    }catch(value){assertCurrent();failures.push(`${file.name}：${errorMessage(value)}`);}
    finally{uploadedBytes+=currentBytes;}
   }
   assertCurrent();
   if(failures.length)throw new Error(failures.join('\n'));
   return results;
  }catch(error){if(destination==='chat'&&isCurrent())for(const file of results)await api.releaseChatAttachment(file.id);throw error;}
  finally{if(isCurrent()){void refreshStorage().catch(()=>{});if(!destination)void refreshLibrary().catch(()=>{});}}
 }
 async function deleteFile(fileId:string){
  assertCurrent();
  try{catalogEpoch.current++;await api.deleteFile(fileId);assertCurrent();catalogEpoch.current++;setFiles(current=>current.filter(file=>file.id!==fileId));void refreshLibrary().catch(()=>{});}
  finally{if(isCurrent())void refreshStorage().catch(()=>{});}
 }
 async function deleteThread(threadId:string){assertCurrent();const stateKey=chatStateKey(`reados.${owner.userId}`,threadId);for(const attachment of readDraftAttachments(stateKey))if(attachment.uploadedFile?.storage==='chat_attachment')await api.releaseChatAttachment(attachment.resourceId);saveDraftAttachments(stateKey,[]);catalogEpoch.current++;await api.deleteThread(threadId);assertCurrent();catalogEpoch.current++;setThreads(current=>current.filter(thread=>thread.id!==threadId));void refreshLibrary().catch(()=>{});}
 return {library,libraryLoading,libraryBusy,libraryError,refreshLibrary,moveLibrary,createLibraryFolder,deleteLibraryFolder,renameLibrary,skills,plugins,visualize:visualizeSettings.value,setVisualize:visualizeSettings.toggle,visualizeSettings,user,setUser,initializing,authBootstrap,files,threads,models,error,setError,health,refresh,refreshThreads,createThread,logout,upload,deleteFile,deleteThread,storage,storageLoading,storageError,refreshStorage};
}
